import { shallowRef, ref, computed, h, Fragment, toRaw, unref, watch } from "vue";
const yandexMapSettings = safeRef({
  apikey: ""
});
const isYandexMapReadyToInit = safeComputed(() => !!yandexMapSettings.value.apikey);
const yandexMapYmaps3 = () => ymaps3;
const yandexMapScript = shallowRef(null);
class YandexMapException extends Error {
  constructor(message) {
    super(message);
    this.message = message;
    this.name = "YandexMapException";
  }
}
const yandexMapLoadStatus = safeRef("pending");
const yandexMapIsLoaded = safeComputed(() => yandexMapLoadStatus.value === "loaded" || yandexMapLoadStatus.value === "error");
const yandexMapLoadError = safeRef(null);
function importLayersExtra() {
  return ymaps3.import("@yandex/ymaps3-layers-extra");
}
function safeRef(value) {
  if (typeof window === "undefined") {
    return {
      // @ts-expect-error SSR workaround
      value,
      __v_isRef: true
    };
  }
  return ref(value);
}
function safeComputed(getter, debugOptions) {
  if (typeof window === "undefined") {
    return {
      get value() {
        return getter();
      },
      // @ts-expect-error SSR workaround
      __v_isRef: true
    };
  }
  return computed(getter, debugOptions);
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function copy(target) {
  target = toRaw(unref(target));
  if (Array.isArray(target)) return target.map((i) => copy(i));
  if (!target || typeof target !== "object" || target?.constructor !== void 0 && target?.constructor !== Object) return target;
  return Object.keys(target).reduce((carry, key) => {
    const val = target[key];
    carry[key] = copy(val);
    return carry;
  }, {});
}
function isDev() {
  return typeof process !== "undefined" && (process.env?.NODE_ENV === "development" || process.dev);
}
function getException({
  text,
  isInternal,
  warn
}) {
  if (warn) {
    text = `Warning: ${text}`;
  }
  if (isInternal) {
    text += " This is likely Vue Yandex Maps internal bug.";
    if (isDev()) {
      text += " You can report this bug here: https://github.com/yandex-maps-unofficial/vue-yandex-maps/issues/new/choose";
    }
  }
  return new YandexMapException(text);
}
function throwException(settings) {
  const exception = getException(settings);
  if (settings.warn) {
    console.warn(exception);
  } else {
    throw exception;
  }
}
function excludeKeys(item, ignoreKeys) {
  for (const [key, value] of Object.entries(item)) {
    if (ignoreKeys.includes(key)) delete item[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      excludeKeys(value, ignoreKeys);
      if (!Object.keys(value).length) delete item[key];
    }
  }
}
function hF(children, props) {
  return h(Fragment, props, children);
}
const allowedOptionsKeys = {
  apikey: true,
  servicesApikeys: true,
  lang: true,
  initializeOn: true,
  importModules: true,
  version: true,
  strictMode: true,
  domain: true,
  mapsRenderWaitDuration: true,
  mapsScriptWaitDuration: true,
  scriptAttributes: true,
  scriptURLParameters: true,
  cdnLibraryLoading: true,
  mapInnerSelector: true
};
function initYmaps() {
  return new Promise((res, rej) => {
    if (typeof ymaps3 !== "undefined") {
      if (yandexMapLoadStatus.value !== "loaded") yandexMapLoadStatus.value = "loaded";
      return res();
    }
    if (typeof window === "undefined") return rej(new YandexMapException("You must call initYmaps on Client Side only"));
    if (yandexMapLoadStatus.value === "loading") {
      const watcher = watch(yandexMapLoadStatus, (val) => {
        if (!yandexMapIsLoaded.value) return;
        watcher();
        if (val === "error") rej(yandexMapLoadError);
        if (val === "loaded") res();
      }, {
        immediate: true
      });
      return;
    }
    yandexMapLoadStatus.value = "loading";
    const settings = yandexMapSettings.value;
    if (!settings.apikey) {
      throwException({
        text: "apikey was not set for Yandex Maps initialization. Ensure you have attached needed plugins or called createYmapsOptions with apikey. If you need delayed init, please use isYandexMapReadyToInit computed as v-if."
      });
    }
    const script = document.createElement("SCRIPT");
    yandexMapScript.value = script;
    const url = new URL(`${settings.domain}/${settings.version}/`);
    for (const key in settings.scriptURLParameters) {
      url.searchParams.set(key, settings.scriptURLParameters[key]);
    }
    url.searchParams.set("lang", settings.lang || "ru_RU");
    url.searchParams.set("apikey", settings.apikey);
    const scriptAttributes = {
      async: "",
      defer: "",
      referrerpolicy: "strict-origin-when-cross-origin",
      type: "text/javascript",
      id: "vue-yandex-maps",
      src: url.toString(),
      ...settings.scriptAttributes || {}
    };
    for (const key in scriptAttributes) {
      if (scriptAttributes[key] === false) continue;
      script.setAttribute(key, scriptAttributes[key]);
    }
    document.head.appendChild(script);
    script.onload = async () => {
      try {
        await yandexMapYmaps3().ready;
        if (settings.servicesApikeys && Object.keys(settings.servicesApikeys).length > 0) {
          yandexMapYmaps3().getDefaultConfig().setApikeys(settings.servicesApikeys);
        }
        if (typeof settings.strictMode === "boolean") yandexMapYmaps3().strictMode = settings.strictMode;
        if (settings.importModules) {
          await Promise.all(
            settings.importModules.map(
              (module) => yandexMapYmaps3().import(module)
            )
          );
        }
        if (settings.cdnLibraryLoading?.enabled !== false) {
          yandexMapYmaps3().import.registerCdn(settings.cdnLibraryLoading?.url || "https://cdn.jsdelivr.net/npm/{package}", [
            ...[
              "@yandex/ymaps3-default-ui-theme@latest",
              "@yandex/ymaps3-resizer@latest",
              "@yandex/ymaps3-minimap@latest",
              "@yandex/ymaps3-context-menu@latest",
              "@yandex/ymaps3-drawer-control@latest",
              "@yandex/ymaps3-signpost@latest",
              "@yandex/ymaps3-spinner@latest",
              "@yandex/ymaps3-world-utils@latest",
              "@yandex/ymaps3-web-mercator-projection@latest",
              "@yandex/ymaps3-cartesian-projection@latest",
              "@yandex/ymaps3-hint@latest",
              "@yandex/ymaps3-clusterer@latest"
            ].filter((x) => !(settings.cdnLibraryLoading?.extendLibraries ?? []).some((y) => {
              if (!y.startsWith("@yandex")) return false;
              return y.split("@")[1] === x.split("@")[1];
            })),
            ...settings.cdnLibraryLoading?.extendLibraries ?? []
          ]);
        }
        yandexMapLoadStatus.value = "loaded";
        res();
      } catch (e) {
        yandexMapLoadStatus.value = "error";
        yandexMapLoadError.value = e;
        rej(e);
      }
    };
    script.onerror = (e) => {
      yandexMapLoadError.value = e;
      rej(e);
    };
  });
}
function createYmapsOptions(options, ignoreNoCurrentInstance = false) {
  if (isYandexMapReadyToInit.value) return yandexMapSettings.value;
  const optionsShallowClone = {
    lang: "ru_RU",
    initializeOn: "onComponentMount",
    importModules: [],
    version: "v3",
    strictMode: false,
    domain: "https://api-maps.yandex.ru",
    mapsRenderWaitDuration: true,
    mapsScriptWaitDuration: true,
    servicesApikeys: null,
    scriptAttributes: {},
    scriptURLParameters: {},
    cdnLibraryLoading: {},
    mapInnerSelector: true,
    ...options
  };
  if (!optionsShallowClone.apikey) {
    throwException({
      text: "You must specify apikey for createYmapsOptions"
    });
  }
  const notAllowedKeys = Object.keys(optionsShallowClone).filter((key) => !(key in allowedOptionsKeys));
  if (notAllowedKeys.length && !notAllowedKeys.every((x) => x === "typesInjection")) {
    throwException({
      text: `You have passed unknown keys to createYmapsOptions: ${notAllowedKeys.join(", ")}. Only ${Object.keys(allowedOptionsKeys).join(", ")} are allowed.`
    });
  }
  if (typeof window === "undefined") return optionsShallowClone;
  yandexMapSettings.value = optionsShallowClone;
  return optionsShallowClone;
}
function importYmapsCDNModule(module) {
  return ymaps3.import(module);
}
export {
  YandexMapException as Y,
  initYmaps as a,
  yandexMapIsLoaded as b,
  createYmapsOptions as c,
  yandexMapLoadStatus as d,
  yandexMapLoadError as e,
  yandexMapScript as f,
  getException as g,
  copy as h,
  importYmapsCDNModule as i,
  excludeKeys as j,
  hF as k,
  importLayersExtra as l,
  isYandexMapReadyToInit as m,
  sleep as s,
  throwException as t,
  yandexMapSettings as y
};
